/**
 * timeline — the pure data layer behind the drawer's Timeline tab (Story J).
 *
 * The tab answers one question two ways, depending on whether the trial is still
 * running. For an IN-FLIGHT trial the daemon now persists a durable, bounded,
 * ordered per-trial phase-event history (W5 backend), exposed as `phaseEvents`
 * on `GET /trials/<id>` with epoch-ms timestamps. {@link buildPhaseTimeline}
 * folds that authoritative history (plus the live `latestPhase` tail) into a
 * timestamped, dwell-annotated sequence — a real progression with per-step
 * `when` + dwell, not a single latest-state snapshot. For a FINISHED trial the
 * full ordered step sequence already exists in the bundle's `trajectory.json`,
 * resolved through Story K's object interface and shaped by {@link parseTrajectory}.
 *
 * Both reconstructions are kept here as pure functions — free of React, fetch,
 * and timers — so the folding and the (deliberately tolerant) trajectory
 * parsing are unit-tested directly; the components are the thin shells that drive
 * them.
 */

import { phaseSubLabel } from './format'
import type { PhaseEvent } from './types'

/** One rendered entry of the in-flight phase timeline. */
export interface PhaseTimelineEntry {
  /** The raw phase token as the daemon emitted it (stable key + tooltip). */
  phase: string
  /** The terse human sub-label (tolerant map; unknown tokens pass through). */
  label: string
  /** Epoch-ms the trial entered this phase. */
  ts: number
  /**
   * Milliseconds spent in this phase before the next began. For the current
   * (last) phase this is the live elapsed dwell (`phaseDwellMs`), or null when no
   * live dwell is known — e.g. after a daemon restart cleared the in-memory tail.
   */
  dwellMs: number | null
  /** The phase the trial is in now — the live tail of the sequence. */
  active: boolean
}

/**
 * Fold a trial's durable phase history into a timestamped, dwell-annotated
 * timeline. `events` is the authoritative ordered (oldest-first) history that
 * survives a daemon restart and is the seed-of-truth; `latestPhase` /
 * `phaseDwellMs` are the live in-memory tail. When the live tail names a phase
 * not yet in the durable history (a persist race), it is appended as the current
 * phase with its start estimated from `now - phaseDwellMs`; while live, the tail
 * already equals the last persisted event, so no synthetic entry is added.
 *
 * Inter-phase dwell is the gap between consecutive event timestamps; the final
 * phase's dwell is the live `phaseDwellMs` (null when unknown). An empty history
 * with no live phase yields an empty timeline, which the tab renders as an honest
 * waiting state rather than a fabricated single step.
 */
export function buildPhaseTimeline(
  events: readonly PhaseEvent[],
  latestPhase: string | null,
  phaseDwellMs: number | null,
  now: number
): PhaseTimelineEntry[] {
  const points = events.map((event) => ({ phase: event.phase, ts: event.ts }))
  const tailPhase = points.length > 0 ? points[points.length - 1].phase : null
  if (latestPhase !== null && latestPhase !== tailPhase) {
    points.push({ phase: latestPhase, ts: phaseDwellMs !== null ? now - phaseDwellMs : now })
  }
  return points.map((point, index) => {
    const next = points[index + 1]
    const active = index === points.length - 1
    const dwellMs = next
      ? Math.max(0, next.ts - point.ts)
      : latestPhase !== null && latestPhase === point.phase
        ? phaseDwellMs
        : null
    return { phase: point.phase, label: phaseSubLabel(point.phase), ts: point.ts, dwellMs, active }
  })
}

/** One parsed step of a finished trial's `trajectory.json`. */
export interface TrajectoryStep {
  /** Zero-based position in the sequence (stable key + display ordinal). */
  index: number
  /** A terse step label (role/type/phase), or `step N` when none is present. */
  label: string
  /** Step body (text/content + any tool calls), or null when the step carries none. */
  detail: string | null
}

/** Keys, in priority order, that a trajectory step may use for its short label. */
const STEP_LABEL_KEYS = ['role', 'type', 'phase', 'name', 'kind', 'action'] as const
/** Keys, in priority order, that a trajectory step may use for its body text. */
const STEP_DETAIL_KEYS = ['text', 'content', 'message', 'note', 'detail', 'observation'] as const
/** Container keys a trajectory document may nest its step array under. */
const STEP_CONTAINER_KEYS = ['turns', 'steps', 'trajectory', 'events', 'messages'] as const
/** Key a trajectory turn nests its tool invocations under (real harness shape). */
const TOOL_CALLS_KEY = 'toolCalls'
/** Keys, in priority order, a single tool call may name its tool with. */
const TOOL_NAME_KEYS = ['toolName', 'name', 'tool'] as const

/**
 * Pull the step array out of a parsed trajectory document. The shape is NOT a
 * closed contract — observed forms are a bare array (the real harness shape: an
 * `Array<{ toolCalls }>` per `src/types/harness.ts` ScenarioResult.trajectories),
 * `{turns:[…]}` (the e2e stub, `src/e2e/guest-entry.ts`), and `{steps:[…]}`. So
 * this is deliberately tolerant: a bare array is the steps; an object is searched
 * for the first array under a known container key; anything else yields no steps
 * (the tab then renders an honest empty state rather than crashing on a novel
 * shape).
 */
function stepArray(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw
  }
  if (raw !== null && typeof raw === 'object') {
    const record = raw as Record<string, unknown>
    for (const key of STEP_CONTAINER_KEYS) {
      const value = record[key]
      if (Array.isArray(value)) {
        return value
      }
    }
  }
  return []
}

/** The first present, non-empty string value among `keys` on `record`, else null. */
function firstString(record: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim().length > 0) {
      return value
    }
  }
  return null
}

/**
 * Extract the tool names a trajectory turn invoked, when it carries the real
 * harness `toolCalls` array (`Array<{ toolName }>`). A tool call may be an object
 * naming its tool under `toolName`/`name`/`tool`, or a bare string. Returns null
 * when the turn made no tool calls, so a plain text turn renders its text alone.
 */
function toolNamesFrom(record: Record<string, unknown>): string[] | null {
  const calls = record[TOOL_CALLS_KEY]
  if (!Array.isArray(calls)) {
    return null
  }
  const names = calls
    .map((call) => {
      if (call !== null && typeof call === 'object') {
        return firstString(call as Record<string, unknown>, TOOL_NAME_KEYS)
      }
      return typeof call === 'string' && call.trim().length > 0 ? call : null
    })
    .filter((name): name is string => name !== null)
  return names.length > 0 ? names : null
}

/** Join a turn's text body and its tool-call summary into one legible detail line. */
function joinDetail(text: string | null, tools: string[] | null): string | null {
  const parts: string[] = []
  if (text !== null) {
    parts.push(text)
  }
  if (tools !== null) {
    parts.push(`tools: ${tools.join(', ')}`)
  }
  return parts.length > 0 ? parts.join(' · ') : null
}

/** Render one raw trajectory entry into a labelled step, tolerant of its shape. */
function toStep(raw: unknown, index: number): TrajectoryStep {
  if (raw === null || typeof raw !== 'object') {
    // A primitive step (string/number) — show it verbatim as the label.
    return { index, label: String(raw), detail: null }
  }
  const record = raw as Record<string, unknown>
  const tools = toolNamesFrom(record)
  const label = firstString(record, STEP_LABEL_KEYS) ?? (tools !== null ? 'tool calls' : `step ${index + 1}`)
  const detail = joinDetail(firstString(record, STEP_DETAIL_KEYS), tools)
  return { index, label, detail }
}

/**
 * Parse a finished trial's `trajectory.json` into an ordered step list. Tolerant
 * by construction (the document shape is not a fixed contract): it extracts the
 * step array from the common shapes — the real harness bare `Array<{ toolCalls }>`,
 * the `{turns:[…]}` stub, `{steps:[…]}` — and renders each entry's short label +
 * body, surfacing invoked tool names and falling back to `step N` / a stringified
 * primitive so a novel shape degrades legibly instead of throwing. An unparseable
 * or step-less document yields an empty array, which the tab renders as an honest
 * empty state.
 */
export function parseTrajectory(raw: unknown): TrajectoryStep[] {
  return stepArray(raw).map((step, index) => toStep(step, index))
}
